import React, { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Edit } from "@mui/icons-material";
import Modal from '@mui/material/Modal';
import CloseIcon from "@mui/icons-material/Close";
import Button from '../../Button/Button';
import InputController from '../../Input/InputField/InputController';
import TelInputController from '../../Input/TelInputField/TelInputController';
import email from '../../../../assets/svgs/email.svg';
import phone_number from '../../../../assets/svgs/phone_number.svg';
import { changeEmail, changePhoneNumber, verifyEmail, verifyPhoneNumber } from '../../../../redux/slices/updateUserSlice';
import { updateEmailAndPhoneModalStyle as styles } from './updateEmailAnsPhoneStyles.tailwind';
import { updateEmailValidationSchema, updatePhoneValidationSchema } from './updateEmailAndPhoneValidationSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import { RESEND_OTP_DURATION } from '../../../../lib/constants/globalConstant';


const UpdateEmailAndPhoneModal = ({ isOpen, onClose, fieldToUpdate, changeEmailAddress, changeNumber, verifyEmailAddress, verifyNumber, updateFieldFromModal }) => {
  const { user } = useSelector((state) => state.user);
  const { changing, verifying } = useSelector((state) => state.updateUser);
  const profileData = user?.userDetails;
  const resendOtpDuration = RESEND_OTP_DURATION;
  const validationSchema = fieldToUpdate === "userEmail"  ? updateEmailValidationSchema : updatePhoneValidationSchema;

  const [previousValue, setPreviousValue] = useState(null);
  const [otpReceived, setOtpReceived] = useState(false);
  const [countdown, setCountdown] = useState(resendOtpDuration);
  const [timerActive, setTimerActive] = useState(false);
  const [isEditable, setIsEditable] = useState(true); 

  const { control, handleSubmit, setValue, setError, watch, formState: { errors } } = useForm({ resolver: yupResolver(validationSchema) });

  const data = {
    heading: fieldToUpdate === "userEmail" ? "Email Address" : "Phone Number",
    previousValue,
    input: fieldToUpdate === "userEmail"
      ? <InputController control={control} showLabel={true} name="userNewEmail" label={previousValue ? "New Email Address" : "Email Address"} placeholder={"Enter Email Address"} error={!!errors?.userNewEmail} helperText={errors?.userNewEmail?.message} disabled={!isEditable} />
      : <TelInputController control={control} name="userPhone" label="Phone Number" error={!!errors?.userPhone} helperText={errors?.userPhone?.message} disabled={!isEditable} />
  };

  // Function to handle form submission if the result is success then close the modal and set the countdown for resend otp
  const onSubmit = async (data) => {
    let response;

    // Call the appropriate API based on fieldToUpdate

    //for mail
    if (fieldToUpdate === "userEmail") {
      const formData = { userOldEmail: previousValue ?? null, userEmail: data.userNewEmail };
      response = await changeEmailAddress(formData);
      if (response?.payload?.status === 'success') {
        setOtpReceived(true);
        setCountdown(resendOtpDuration);
        setTimerActive(true);
        setIsEditable(false);
      }
    } else {
      // for number
      const formData = { userOldCellNumber: previousValue ?? null, userCellNumber: `+${data.userPhone}` };
      response = await changeNumber(formData);
      if (response?.payload?.status === 'success') {
        setOtpReceived(true);
        setCountdown(resendOtpDuration);
        setTimerActive(true);
        setIsEditable(false);
      }
    }
  };

  // on verification of otp the validations are added and the modal is closed after the success
  const onVerify = async (data) => {
    if (!data.otp) {
      setError("otp", {
        type: "manual",
        message: "OTP is required.",
      });
      return;
    }

    // Check if OTP is not exactly 6 digits
    if (data.otp.length !== 6 || !/^\d{6}$/.test(data.otp)) {
      setError("otp", {
        type: "manual",
        message: "Please enter a valid 6-digit OTP.",
      });
      return;
    }

    if (fieldToUpdate === "userEmail") {
      const formData = { otp: data.otp, userEmail: data.userNewEmail };
      const response = await verifyEmailAddress(formData);
      if (response?.payload?.status === 'success') {
        updateFieldFromModal(fieldToUpdate, data.userNewEmail);
        onClose();
      }
    } else {
      const formData = { otp: data.otp, userCellNumber: `+${data.userPhone}` };
      const response = await verifyNumber(formData);
      if (response?.payload?.status === 'success') {
        updateFieldFromModal(fieldToUpdate, data.userPhone);
        onClose();
      }
    }
  };

// handle otp to just enter the digits and not let the user enter more than 6
  const handleOtpChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length > 6) {
      value = value.slice(0, 6);
    }
    setValue("otp", value);
  };

  const handleEditClick = () => {
    setIsEditable(true);
    setOtpReceived(false); 
    setValue("otp", "");
  };

  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (otpReceived) {
        handleSubmit(onVerify)();
      } else {
        handleSubmit(onSubmit)();
      }
    }
  };

  
  // Timer for resend otp
  useEffect(() => {
    if (otpReceived && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(timer);
    } else if (countdown === 0) {
      setTimerActive(false);
    }
  }, [otpReceived, countdown]);

  // set the previous value
  useEffect(() => {
    if (fieldToUpdate === "userEmail") {
      setPreviousValue(profileData?.userEmail);
    } else {
      setPreviousValue(profileData?.userCellNumber);
    }
  }, [fieldToUpdate, profileData]);

  // Set default values when previousValue is available
  useEffect(() => {
    if (previousValue) {
      setValue("previousValue", previousValue);
    }
  }, [previousValue, setValue]);

  return (
    <Modal
      open={isOpen}
      onClose={() => {}}
      sx={{ padding: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      disableAutoFocus
      disableEnforceFocus
    >
      <div className={styles.container}>
        <button onClick={onClose} className={styles.closeButton}>
          <CloseIcon sx={{ fontSize: "16px" }} />
        </button>

        <div className={styles.img_heading}>
          <img src={fieldToUpdate === "userEmail" ? email : phone_number } alt="email" className={styles.popup_img}></img>
          <div className={styles.heading}>{data.previousValue ? "Update " : "Add "}{data.heading}</div>
        </div>
        {/* if mail then input controller for email */}
        {data.previousValue && fieldToUpdate === "userEmail" && (
          <InputController
            control={control}
            showLabel={true}
            name="previousValue"
            label={`Previous ${data.heading}`}
            value={data.previousValue}
            disabled
          />
        )}

        {/* if number then telInputController for number */}
        {data.previousValue && fieldToUpdate === "userNumber" && (
          <TelInputController
            control={control}
            showLabel={true}
            name="previousValue"
            label={`Previous ${data.heading}`}
            value={data.previousValue}
            disabled
          />
        )}

        <form className={styles.formContainer} onKeyDown={handleFormKeyDown} >
          <div className={styles.newValueContainer}>
            {otpReceived && (
              <button className={`absolute text-[12px] right-1 flex items-center text-primaryHeading hover:underline cursor-pointer z-10 ${fieldToUpdate === "userEmail" ? "-top-1 " : "-top-[14px]"}`} onClick={handleEditClick}>
                <Edit fontSize="10" />
                Edit
              </button>
            )}
            {data.input}

            {otpReceived && (
              <div className={styles.afterOtpreceivedContainer}>
                {/* Common spam folder reminder */}
                <div className={styles.spamNote}>
                  OTP sent. Look in inbox or spam.
                </div>

                {/* Dynamic resend content */}
                {timerActive ? (
                  <div className={styles.resendOtp}>
                    Resend OTP in {countdown}s
                  </div>
                ) : (
                  countdown === 0 && (
                    <button
                      className={`${styles.resendOtp}`}
                      disabled={changing}
                      type="button"
                      onClick={handleSubmit(onSubmit)}
                    >
                      {changing ? "Resending OTP..." : "Resend OTP"}
                    </button>
                  )
                )}
              </div>
            )}
          </div>

          {!otpReceived && (
            <Button
              variant="primary"
              className={styles.button}
              onClick={handleSubmit(onSubmit)}
              disabled={changing}
            >
              {changing ? "Sending OTP..." : "Send OTP"}
            </Button>
          )}

          {otpReceived && (
            <div className={styles.otpContainer}>
              <InputController
                control={control}
                showLabel={true}
                name="otp"
                label="Enter OTP"
                type="number"
                onChange={handleOtpChange}
                error={!!errors?.otp} // Pass error state
                helperText={errors?.otp?.message} // Pass error message
              />
              <Button
                variant="primary"
                className={styles.button}
                onClick={handleSubmit(onVerify)}
                disabled={verifying || watch('otp')?.length !== 6}
              >
                {verifying ? "Verifying..." : "Verify"}
              </Button>
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    changeEmailAddress: (data) => dispatch(changeEmail(data)),
    changeNumber: (data) => dispatch(changePhoneNumber(data)),
    verifyEmailAddress: (data) => dispatch(verifyEmail(data)),
    verifyNumber: (data) => dispatch(verifyPhoneNumber(data)),
  };
};

export default connect(null, mapDispatchToProps)(UpdateEmailAndPhoneModal);
